import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { 
  MoreVertical,
  Crown,
  Shield,
  UserCheck,
  UserX,
  Ban,
  MessageSquare,
  UserMinus,
  AlertTriangle
} from 'lucide-react';

interface Member {
  user_id: string;
  role: string;
  profiles?: {
    first_name?: string | null;
    last_name?: string | null;
  } | null;
}

interface AdminMemberActionsProps {
  member: Member;
  currentUserId: string;
  roomCreatedBy: string | null;
  isRoomCreator: boolean;
  isCurrentUserAdmin: boolean;
  isCurrentUserModerator: boolean;
  onPromoteMember: (userId: string, role: 'admin' | 'moderator') => void;
  onDemoteMember: (userId: string) => void;
  onRemoveMember: (userId: string) => void;
  onKickMember?: (userId: string) => void;
  onBanMember?: (userId: string) => void;
  onMuteMessage?: (userId: string) => void;
}

export default function AdminMemberActions({
  member,
  currentUserId,
  roomCreatedBy,
  isRoomCreator,
  isCurrentUserAdmin,
  isCurrentUserModerator,
  onPromoteMember,
  onDemoteMember,
  onRemoveMember,
  onKickMember,
  onBanMember,
  onMuteMessage
}: AdminMemberActionsProps) {
  const [showConfirmDialog, setShowConfirmDialog] = useState<string | null>(null);

  const canManage = () => {
    if (member.user_id === currentUserId) return false; // Can't manage yourself
    if (member.user_id === roomCreatedBy) return false; // Can't manage room creator
    
    if (isRoomCreator) return true; // Room creator can manage anyone
    if (isCurrentUserAdmin && member.role !== 'admin') return true; // Admins can manage non-admins
    if (isCurrentUserModerator && member.role === 'member') return true; // Moderators can manage members only
    
    return false;
  };

  const getUserDisplayName = () => {
    const profile = member.profiles;
    if (profile?.first_name || profile?.last_name) {
      return `${profile.first_name || ''} ${profile.last_name || ''}`.trim();
    }
    return 'Unknown User';
  };

  if (!canManage()) {
    return null;
  }

  const handleAction = (action: () => void, confirmType?: string) => {
    if (confirmType) {
      setShowConfirmDialog(confirmType);
    } else {
      action();
    }
  };

  const confirmAction = (action: () => void) => {
    action();
    setShowConfirmDialog(null);
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
            <MoreVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          
          {/* Promotion Options */}
          {member.role === 'member' && (
            <>
              {isCurrentUserAdmin && (
                <DropdownMenuItem 
                  onClick={() => onPromoteMember(member.user_id, 'moderator')}
                  className="text-blue-600 dark:text-blue-400"
                >
                  <Shield className="h-4 w-4 mr-2" />
                  Make Moderator
                </DropdownMenuItem>
              )}
              {isRoomCreator && (
                <DropdownMenuItem 
                  onClick={() => onPromoteMember(member.user_id, 'admin')}
                  className="text-purple-600 dark:text-purple-400"
                >
                  <Crown className="h-4 w-4 mr-2" />
                  Make Admin
                </DropdownMenuItem>
              )}
            </>
          )}
          
          {member.role === 'moderator' && isCurrentUserAdmin && (
            <>
              {isRoomCreator && (
                <DropdownMenuItem 
                  onClick={() => onPromoteMember(member.user_id, 'admin')}
                  className="text-purple-600 dark:text-purple-400"
                >
                  <Crown className="h-4 w-4 mr-2" />
                  Promote to Admin
                </DropdownMenuItem>
              )}
              <DropdownMenuItem 
                onClick={() => onDemoteMember(member.user_id)}
                className="text-orange-600 dark:text-orange-400"
              >
                <UserCheck className="h-4 w-4 mr-2" />
                Demote to Member
              </DropdownMenuItem>
            </>
          )}
          
          {member.role === 'admin' && isRoomCreator && (
            <DropdownMenuItem 
              onClick={() => onDemoteMember(member.user_id)}
              className="text-orange-600 dark:text-orange-400"
            >
              <UserCheck className="h-4 w-4 mr-2" />
              Demote to Member
            </DropdownMenuItem>
          )}
          
          {/* Moderation Actions */}
          {(member.role === 'member' || (member.role === 'moderator' && isCurrentUserAdmin)) && (
            <>
              <DropdownMenuSeparator />
              
              {onMuteMessage && (
                <DropdownMenuItem 
                  onClick={() => onMuteMessage(member.user_id)}
                  className="text-yellow-600 dark:text-yellow-400"
                >
                  <MessageSquare className="h-4 w-4 mr-2" />
                  Mute Messages
                </DropdownMenuItem>
              )}
              
              {onKickMember && (
                <DropdownMenuItem 
                  onClick={() => handleAction(() => onKickMember(member.user_id), 'kick')}
                  className="text-orange-600 dark:text-orange-400"
                >
                  <UserMinus className="h-4 w-4 mr-2" />
                  Kick Member
                </DropdownMenuItem>
              )}
              
              {onBanMember && isCurrentUserAdmin && (
                <DropdownMenuItem 
                  onClick={() => handleAction(() => onBanMember(member.user_id), 'ban')}
                  className="text-red-600 dark:text-red-400"
                >
                  <Ban className="h-4 w-4 mr-2" />
                  Ban Member
                </DropdownMenuItem>
              )}
            </>
          )}
          
          <DropdownMenuSeparator />
          
          {/* Remove Member */}
          <DropdownMenuItem 
            onClick={() => handleAction(() => onRemoveMember(member.user_id), 'remove')}
            className="text-destructive focus:text-destructive"
          >
            <UserX className="h-4 w-4 mr-2" />
            Remove Member
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Confirmation Dialogs */}
      <AlertDialog open={showConfirmDialog !== null} onOpenChange={() => setShowConfirmDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-orange-500" />
              {showConfirmDialog === 'remove' && 'Remove Member'}
              {showConfirmDialog === 'kick' && 'Kick Member'}
              {showConfirmDialog === 'ban' && 'Ban Member'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {showConfirmDialog === 'remove' && (
                <>
                  Are you sure you want to remove {getUserDisplayName()} from this chat room?
                  They will no longer be able to see messages or participate in the conversation.
                </>
              )}
              {showConfirmDialog === 'kick' && (
                <>
                  Are you sure you want to kick {getUserDisplayName()} from this chat room?
                  They can rejoin if the room is public or if they're invited again.
                </>
              )}
              {showConfirmDialog === 'ban' && (
                <>
                  Are you sure you want to ban {getUserDisplayName()} from this chat room?
                  This will permanently prevent them from joining this room.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (showConfirmDialog === 'remove') confirmAction(() => onRemoveMember(member.user_id));
                if (showConfirmDialog === 'kick' && onKickMember) confirmAction(() => onKickMember(member.user_id));
                if (showConfirmDialog === 'ban' && onBanMember) confirmAction(() => onBanMember(member.user_id));
              }}
              className={`${showConfirmDialog === 'ban' ? 'bg-red-600 hover:bg-red-700' : 'bg-destructive hover:bg-destructive/90'} text-white`}
            >
              {showConfirmDialog === 'remove' && 'Remove Member'}
              {showConfirmDialog === 'kick' && 'Kick Member'}
              {showConfirmDialog === 'ban' && 'Ban Member'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}